import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../components/UI/Buttons/Button/Button";
import { selectTokenUserId } from "../../store/auth-slice";
import {
  consultPharmacyContextActions,
  selectCanActivateAvailability,
  selectConsultPharmacyState,
} from "../../store/consultPharmacyContext-slice";
import {
  PharmacistProfileFetchResult,
  fetchPharmacistProfile,
} from "../../store/consultPharmacyContext-actions";
import {
  buildImagesCreate,
  buildImagesUpdate,
  createPharmacistProfile,
  PendingMediaFile,
  setPharmacistAvailability,
  updatePharmacistProfile,
  updateWorkingHours,
} from "../../services/pharmacistProfileService";
import {
  PharmacistProfileFileResponse,
  PharmacistProfileRequest,
  PharmacistProfileResponse,
  PharmacistWorkingHoursEntry,
} from "../../types/pharmacistProfile.types";
import {
  GENERIC_LOAD_ERROR,
  NO_PHARMACY_NOTICE,
  PROFILE_GONE_ERROR,
  isAssociationRequired,
  mapAvailabilityError,
  mapProfileSaveError,
  mapWorkingHoursError,
  shouldRefetchAfterError,
} from "../../utils/pharmacistProfileErrors";
import MediaManager from "./MediaManager";
import MissedCallsPanel from "./MissedCallsPanel";
import PharmacistStatsPanel from "./PharmacistStatsPanel";
import WorkingHoursEditor, {
  HoursDraft,
  buildHoursDraft,
  hasUsableSchedule,
} from "./WorkingHoursEditor";
import styles from "./PharmacistProfilePage.module.scss";

// ITAM-2622 (3.9) — the pharmacist's self-managed adviser profile.
//
// Three top-level states (404 -> create, profile-not-enrolled, profile+enrolled)
// plus an enrolled-but-no-schedule sub-state. The own user id comes EXCLUSIVELY
// from the JWT, never from a route or a form field.
//
// Every mutating control carries a pending lock and disables while its request
// is in flight, so a double-click fires exactly ONE request. Those guards
// prevent FE-originated duplicates of the backend's two check-then-insert races
// (BG-7); monitoring detects the rest.

const MAX_TEXT = 64;
const STATUS_REFETCH_MS = 30000;

const AVAILABILITY_LABEL: Record<string, string> = {
  FREE: "Slobodan",
  IN_CALL: "U pozivu",
  UNAVAILABLE: "Nedostupan",
};

interface IdentityDraft {
  firstName: string;
  secondName: string;
  licenceNumber: string;
  biography: string;
}

const identityFrom = (
  profile: PharmacistProfileResponse | null
): IdentityDraft => ({
  firstName: profile?.firstName ?? "",
  secondName: profile?.secondName ?? "",
  licenceNumber: profile?.licenceNumber ?? "",
  biography: profile?.biography ?? "",
});

type IdentityErrors = Partial<Record<keyof IdentityDraft, string>>;

/** Decision 19: ime, prezime, broj licence non-blank and at most 64 characters. */
const validateIdentity = (draft: IdentityDraft): IdentityErrors => {
  const errors: IdentityErrors = {};
  const required: Array<[keyof IdentityDraft, string]> = [
    ["firstName", "Unesite ime."],
    ["secondName", "Unesite prezime."],
    ["licenceNumber", "Unesite broj licence."],
  ];
  required.forEach(([field, message]) => {
    const value = draft[field].trim();
    if (!value) {
      errors[field] = message;
    } else if (value.length > MAX_TEXT) {
      errors[field] = `Najviše ${MAX_TEXT} karaktera.`;
    }
  });
  return errors;
};

const PharmacistProfileEditor: React.FC = () => {
  const dispatch = useDispatch();
  const userId = useSelector(selectTokenUserId);
  const { profile, pair, status, loadError } = useSelector(
    selectConsultPharmacyState
  );
  const canActivate = useSelector(selectCanActivateAvailability);

  const [identity, setIdentity] = useState<IdentityDraft>(() =>
    identityFrom(profile)
  );
  const [identityErrors, setIdentityErrors] = useState<IdentityErrors>({});
  const [keptMedia, setKeptMedia] = useState<PharmacistProfileFileResponse[]>(
    profile?.images ?? []
  );
  const [pendingMedia, setPendingMedia] = useState<PendingMediaFile[]>([]);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const [hoursDraft, setHoursDraft] = useState<HoursDraft>(() =>
    buildHoursDraft(profile?.workingHours)
  );

  const [savingProfile, setSavingProfile] = useState(false);
  const [savingHours, setSavingHours] = useState(false);
  const [savingAvailability, setSavingAvailability] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [hoursError, setHoursError] = useState<string | null>(null);
  const [hoursSaved, setHoursSaved] = useState(false);
  const [availabilityError, setAvailabilityError] = useState<string | null>(
    null
  );
  const [availabilityNotice, setAvailabilityNotice] = useState<string | null>(
    null
  );

  // Re-seeding is SCOPED to what a mutation owns, and every call site is
  // explicit, because the two ways of getting here need opposite things:
  //
  //  - a background arrival (the 30 s status refetch) must NOT touch the editor,
  //    or it would delete whatever the pharmacist is halfway through typing;
  //  - the refetch that FOLLOWS a mutation must rebase the editor on server
  //    truth, or the fields keep showing what was sent rather than what was
  //    stored — and `pendingMedia`, left un-cleared, is uploaded a SECOND time
  //    by the next save.
  //
  // Keying the effect below on `profile.id` gets the first half right and the
  // second half wrong on its own, since an update to an existing profile returns
  // the same id and the effect never re-runs. Hence the explicit seeders.
  const seedIdentityAndMedia = useCallback(
    (next: PharmacistProfileResponse | null) => {
      setIdentity(identityFrom(next));
      setKeptMedia(next?.images ?? []);
      setPendingMedia([]);
    },
    []
  );
  const seedHours = useCallback((next: PharmacistProfileResponse | null) => {
    setHoursDraft(buildHoursDraft(next?.workingHours));
  }, []);

  // ...and re-seeding is CONDITIONAL on the reconciliation GET having found a
  // profile. A 404 there proves only that the server holds nothing; it says
  // nothing about what the pharmacist typed. Adopting that null would empty the
  // onboarding form and drop the selected files underneath the very error
  // message asking them to try again — which is precisely the first-time create
  // path, where a bodyless 400 and a 404 arrive together. The id-keyed effect
  // below still owns the legitimate empty first render.
  const adopt = useCallback(
    (
      result: PharmacistProfileFetchResult,
      seed: (next: PharmacistProfileResponse) => void
    ) => {
      if (result.ok && result.profile != null) {
        seed(result.profile);
      }
    },
    []
  );

  // Initial load, and any change of WHICH profile is loaded (404 -> created).
  // Deliberately id-keyed, so background arrivals are inert.
  const profileId = profile?.id ?? null;
  useEffect(() => {
    if (profile == null) {
      // A CONFIRMED absence: a failed GET leaves the previous profile in place,
      // so reaching null always means the server answered and found nothing.
      // Split the state by who owns it — media rows belonged to the profile
      // that is gone and must not be advertised, since the create path sends
      // `pendingMedia` only and would silently drop them; identity, pending
      // uploads and the hours draft are the pharmacist's unsaved work and stay.
      // This lives in the effect, not in the mutation handlers, because null is
      // also reached by the 30 s poll and the availability re-read.
      setKeptMedia((current) => (current.length === 0 ? current : []));
      return;
    }
    seedIdentityAndMedia(profile);
    seedHours(profile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId]);

  const refetch = useCallback(async (): Promise<PharmacistProfileFetchResult> => {
    if (userId == null) {
      return { ok: false };
    }
    // An explicit refetch is also the demand that clears a stuck enrichment
    // error (3.4 retry-on-demand); it is a no-op unless one is stuck.
    dispatch(consultPharmacyContextActions.detailRetryRequested());
    return dispatch<any>(
      fetchPharmacistProfile(userId)
    ) as Promise<PharmacistProfileFetchResult>;
  }, [dispatch, userId]);

  // (d) a light 30 s refetch while the page is visible — no global polling.
  useEffect(() => {
    if (userId == null || profile == null) {
      return;
    }
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") {
        dispatch<any>(fetchPharmacistProfile(userId));
      }
    }, STATUS_REFETCH_MS);
    return () => clearInterval(interval);
  }, [dispatch, userId, profile]);

  // Decision 21: at least one VALID interval, not merely a non-empty schedule.
  const hasSchedule = hasUsableSchedule(profile?.workingHours);
  const enrolled = profile?.availability != null;
  const paused = profile?.paused === true;

  // ONE gate for BOTH `available=true` controls — initial activation and the
  // paused-state resume — so the two can never drift apart again: capability
  // plus the complete pair (`canActivate`), a usable schedule, and no
  // availability mutation in flight. Pause is never gated by any of this;
  // going unavailable is always safe.
  const canSubmitAvailabilityTrue =
    canActivate && hasSchedule && !savingAvailability;
  const mediaChanged =
    pendingMedia.length > 0 ||
    (profile?.images?.length ?? 0) !== keptMedia.length;

  const buildRequest = useCallback(
    (forCreate: boolean): PharmacistProfileRequest | null => {
      if (userId == null) {
        return null;
      }
      const base: PharmacistProfileRequest = {
        userId,
        firstName: identity.firstName.trim(),
        secondName: identity.secondName.trim(),
        licenceNumber: identity.licenceNumber.trim(),
        biography: identity.biography.trim(),
      };
      if (!forCreate && profile?.id != null) {
        base.id = profile.id;
      }
      // A profile save must NEVER replace working hours: omitting the key
      // leaves the schedule alone, while sending [] would clear it.
      return base;
    },
    [identity, profile, userId]
  );

  const submitProfile = async (forCreate: boolean) => {
    if (savingProfile || userId == null) {
      return;
    }
    const errors = validateIdentity(identity);
    setIdentityErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }
    const request = buildRequest(forCreate);
    if (!request) {
      return;
    }
    setSavingProfile(true);
    setProfileError(null);
    try {
      if (forCreate) {
        const { images, files } = buildImagesCreate(pendingMedia);
        await createPharmacistProfile({ ...request, images }, files);
      } else {
        const { images, files } = buildImagesUpdate(
          userId,
          keptMedia,
          pendingMedia
        );
        await updatePharmacistProfile(
          mediaChanged
            ? { ...request, images, updateImages: true }
            : request,
          mediaChanged ? files : []
        );
      }
      // Post-mutation refetch through the SHARED thunk, so the pharmacy context
      // converges without a reload: 404 -> create -> activation enabled. Rebase
      // the editor on what came back — in particular this is what turns the
      // just-uploaded `pendingMedia` into server-backed rows, so a second save
      // does not upload the same files again. A failed GET rebases nothing.
      const result = await refetch();
      adopt(result, seedIdentityAndMedia);
    } catch (error) {
      if (shouldRefetchAfterError(error)) {
        // A bodyless 400 cannot distinguish "already exists" from a validation
        // or upload failure, and (pre-BG-5) an update can COMMIT partial text
        // while still returning 400 — so re-render from refetched truth WHEN
        // there is any. On a first-time create the GET usually 404s, and the
        // draft has to survive that.
        const result = await refetch();
        adopt(result, seedIdentityAndMedia);
        // `result.ok` matters: a failed reconciliation GET must not be read as
        // "no profile exists", which is what a bare null once meant here.
        if (forCreate && result.ok && result.profile != null) {
          setProfileError(
            "Profil već postoji — učitali smo postojeće podatke."
          );
          setSavingProfile(false);
          return;
        }
        if (!forCreate && result.ok && result.profile == null) {
          // Only NOW is "the profile is gone" a fact rather than a status code.
          // The page has switched to the create form, so the copy can point at
          // it. When the GET failed instead, the editor is still the only thing
          // on screen and this message would name a button that is not there.
          setProfileError(PROFILE_GONE_ERROR);
          setSavingProfile(false);
          return;
        }
      }
      setProfileError(mapProfileSaveError(error));
    } finally {
      setSavingProfile(false);
    }
  };

  const submitHours = async (entries: PharmacistWorkingHoursEntry[]) => {
    if (savingHours || userId == null) {
      return;
    }
    setSavingHours(true);
    setHoursError(null);
    setHoursSaved(false);
    try {
      await updateWorkingHours(userId, entries);
      // Only the hours: an identity edit in progress is none of this
      // mutation's business.
      const result = await refetch();
      adopt(result, seedHours);
      setHoursSaved(true);
    } catch (error) {
      if (shouldRefetchAfterError(error)) {
        const result = await refetch();
        adopt(result, seedHours);
        if (result.ok && result.profile == null) {
          // Create mode unmounts the hours editor, so an hours-scoped error
          // would be set and never seen. Surface it where the pharmacist is
          // actually looking.
          setProfileError(PROFILE_GONE_ERROR);
          setSavingHours(false);
          return;
        }
      }
      setHoursError(mapWorkingHoursError(error));
    } finally {
      setSavingHours(false);
    }
  };

  const changeAvailability = async (available: boolean) => {
    if (savingAvailability || userId == null) {
      return;
    }
    // Belt and braces under the disabled buttons: available=true must be
    // unreachable while the activation gate is closed, however the click
    // arrives. Pause (available=false) stays exempt.
    if (available && !canSubmitAvailabilityTrue) {
      return;
    }
    setSavingAvailability(true);
    setAvailabilityError(null);
    setAvailabilityNotice(null);
    const wasInCall = profile?.availability === "IN_CALL";
    try {
      await setPharmacistAvailability(userId, available);
      // No re-seed: this mutation owns neither the identity, the media nor the
      // hours, and rebasing them here would silently discard an edit the
      // pharmacist has not saved yet.
      await refetch();
      if (!available && wasInCall) {
        // BF-13: pausing during a call preserves the current call and takes
        // effect when it ends.
        setAvailabilityNotice("Pauza počinje po završetku poziva.");
      }
    } catch (error) {
      if (isAssociationRequired(error)) {
        // The pharmacy pair changed underneath us. Re-read it so the notice and
        // the disabled activation button appear now, instead of on the next
        // 30 s poll. `refetch` without `adopt` does not rebase the editor, so an
        // unsaved identity or media edit survives.
        const result = await refetch();
        // If the re-read confirms the pair is gone, the page-level notice now
        // carries this exact sentence; repeating it under the button is noise.
        setAvailabilityError(
          result.ok && result.profile?.marketplaceId == null
            ? null
            : mapAvailabilityError(error)
        );
      } else {
        setAvailabilityError(mapAvailabilityError(error));
      }
    } finally {
      setSavingAvailability(false);
    }
  };

  const identityFields = useMemo(
    () =>
      [
        { key: "firstName" as const, label: "Ime" },
        { key: "secondName" as const, label: "Prezime" },
        { key: "licenceNumber" as const, label: "Broj licence" },
      ],
    []
  );

  const renderIdentityForm = (forCreate: boolean) => (
    <section className={styles.card} aria-labelledby="pharmacist-profile-title">
      <h2 id="pharmacist-profile-title" className={styles.cardTitle}>
        {forCreate ? "Kreirajte profil savetnika" : "Podaci savetnika"}
      </h2>
      {forCreate && (
        <p className={styles.hint}>
          Kreirajte profil savetnika da biste primali online konsultacije.
        </p>
      )}

      {identityFields.map((field) => (
        <label key={field.key} className={styles.field}>
          <span className={styles.fieldLabel}>{field.label}</span>
          <input
            type="text"
            className={styles.input}
            maxLength={MAX_TEXT}
            value={identity[field.key]}
            disabled={savingProfile}
            onChange={(e) =>
              setIdentity((prev) => ({ ...prev, [field.key]: e.target.value }))
            }
          />
          {identityErrors[field.key] && (
            <span className={styles.fieldError} role="alert">
              {identityErrors[field.key]}
            </span>
          )}
        </label>
      ))}

      <label className={styles.field}>
        <span className={styles.fieldLabel}>Biografija</span>
        <textarea
          className={styles.textarea}
          rows={5}
          value={identity.biography}
          disabled={savingProfile}
          onChange={(e) =>
            setIdentity((prev) => ({ ...prev, biography: e.target.value }))
          }
        />
      </label>

      <MediaManager
        kept={keptMedia}
        pending={pendingMedia}
        disabled={savingProfile}
        onKeptChange={setKeptMedia}
        onPendingChange={setPendingMedia}
        error={mediaError}
        onError={setMediaError}
      />

      {profileError && (
        <p className={styles.error} role="alert">
          {profileError}
        </p>
      )}

      <Button
        buttonType="blue"
        disabled={savingProfile}
        onClick={() => submitProfile(forCreate)}
      >
        {savingProfile
          ? "Čuvanje..."
          : forCreate
          ? "Kreiraj profil"
          : "Sačuvaj podatke"}
      </Button>
    </section>
  );

  const renderPharmacyNotice = () =>
    pair == null ? (
      <section className={styles.notice} role="note">
        <p>{NO_PHARMACY_NOTICE}</p>
        <Button
          buttonType="white"
          onClick={() => {
            void refetch();
          }}
        >
          Proveri ponovo
        </Button>
      </section>
    ) : null;

  const renderAvailabilityPanel = () => {
    if (!enrolled) {
      const blockedBySchedule = !hasSchedule;
      return (
        <section
          className={styles.card}
          aria-labelledby="pharmacist-availability-title"
        >
          <h2 id="pharmacist-availability-title" className={styles.cardTitle}>
            Dostupnost
          </h2>
          <p className={styles.hint}>Niste prijavljeni za online konsultacije.</p>
          {blockedBySchedule && (
            <p className={styles.fieldError} role="note">
              Unesite radno vreme pre aktivacije — bez rasporeda nećete biti
              dostupni.
            </p>
          )}
          {availabilityError && (
            <p className={styles.error} role="alert">
              {availabilityError}
            </p>
          )}
          <Button
            buttonType="blue"
            disabled={!canSubmitAvailabilityTrue}
            onClick={() => changeAvailability(true)}
          >
            {savingAvailability ? "Aktivacija..." : "Aktiviraj dostupnost"}
          </Button>
        </section>
      );
    }

    return (
      <section
        className={styles.card}
        aria-labelledby="pharmacist-availability-title"
      >
        <h2 id="pharmacist-availability-title" className={styles.cardTitle}>
          Dostupnost
        </h2>
        <p className={styles.statusStrip}>
          <span className={styles.statusValue}>
            {AVAILABILITY_LABEL[profile?.availability ?? ""] ?? "Nedostupan"}
          </span>
          {paused && <span className={styles.pausedBadge}>Na pauzi</span>}
        </p>
        <p className={styles.hint}>
          Dostupnost se menja prema rasporedu (do 5 minuta kašnjenja).
        </p>
        {!hasSchedule && (
          <p className={styles.fieldError} role="note">
            Nemate uneto radno vreme — nećete biti dostupni dok ga ne unesete.
          </p>
        )}
        {availabilityNotice && (
          <p className={styles.notice} role="status">
            {availabilityNotice}
          </p>
        )}
        {availabilityError && (
          <p className={styles.error} role="alert">
            {availabilityError}
          </p>
        )}
        {paused ? (
          <>
            {/* Resume shares the FULL activation gate — schedule included;
                pause never does: going unavailable is always safe. */}
            <Button
              buttonType="blue"
              disabled={!canSubmitAvailabilityTrue}
              onClick={() => changeAvailability(true)}
            >
              {savingAvailability ? "Slanje..." : "Dostupan sam"}
            </Button>
            <p className={styles.hint}>
              Nakon nastavka dostupni ste u okviru svog radnog vremena.
            </p>
          </>
        ) : (
          <Button
            buttonType="white"
            disabled={savingAvailability}
            onClick={() => changeAvailability(false)}
          >
            {savingAvailability ? "Slanje..." : "Idi na pauzu"}
          </Button>
        )}
      </section>
    );
  };

  if (userId == null) {
    return (
      <div className={styles.page}>
        <p className={styles.error}>Sesija nije dostupna.</p>
      </div>
    );
  }

  if (status === "idle" || (status === "loading" && profile == null)) {
    return (
      <div className={styles.page}>
        <p>Učitavanje profila savetnika...</p>
      </div>
    );
  }

  if (status === "error" && profile == null) {
    return (
      <div className={styles.page}>
        <p className={styles.error} role="alert">
          {GENERIC_LOAD_ERROR}
        </p>
        <Button
          buttonType="blue"
          onClick={() => {
            void refetch();
          }}
        >
          Pokušaj ponovo
        </Button>
      </div>
    );
  }

  // State 1 — no profile row yet (404). No association notice here: the
  // backend/admin cannot attach a pharmacy before the profile row exists, so
  // the warning (and its re-check control) would be asking the pharmacist to
  // wait on a step that comes AFTER the one on screen.
  if (status === "noProfile" || profile == null) {
    return (
      <div className={styles.page}>
        <h1 className={styles.pageTitle}>Profil savetnika</h1>
        {renderIdentityForm(true)}
      </div>
    );
  }

  // States 2 and 3 — profile exists; enrollment decides which panel renders.
  return (
    <div className={styles.page}>
      <h1 className={styles.pageTitle}>Profil savetnika</h1>
      {loadError && (
        <p className={styles.error} role="alert">
          {GENERIC_LOAD_ERROR}
        </p>
      )}
      {renderPharmacyNotice()}
      {/* The panels are a grid, not a stack, so the profile fits on a screen.
          The page title, the load error and the pharmacy notice stay outside
          it: they speak for the whole page, not for one panel. */}
      <div className={styles.panelGrid}>
        {renderAvailabilityPanel()}
        <PharmacistStatsPanel userId={userId} />
        {/* ITAM-2746. No props beyond layout: the missed-calls queue is global
            by contract, so passing the user id would imply a scoping the
            endpoint does not do. Its table is too wide to share a row. */}
        <MissedCallsPanel className={styles.panelWide} />
        {renderIdentityForm(false)}
        <WorkingHoursEditor
          draft={hoursDraft}
          onChange={setHoursDraft}
          onSave={submitHours}
          saving={savingHours}
          saveError={hoursError}
          savedNotice={hoursSaved}
        />
      </div>
    </div>
  );
};

/**
 * Identity, media and hours are per-account LOCAL state. `userId` comes from the
 * JWT, so keying the editor on it discards the previous pharmacist's draft by
 * construction — no reset effect, and no dependence on hook declaration order.
 * A reset effect declared after the profile-seeding effect would seed a
 * preloaded profile and then immediately blank it.
 */
const PharmacistProfilePage: React.FC = () => {
  const userId = useSelector(selectTokenUserId);
  return <PharmacistProfileEditor key={userId ?? "anonymous"} />;
};

export default PharmacistProfilePage;
